import { FC, useState, useEffect, useMemo, useCallback } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CodeIcon from "@mui/icons-material/Code";
import DescriptionIcon from "@mui/icons-material/Description";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import DOMPurify from "dompurify";
import {
	useCreateTreatmentPlan,
	useUpdateTreatmentPlan,
} from "../../hooks/useTreatmentPlans";
import { useRewardsEngine } from "../../hooks/useRewardsEngine";
import { useCups } from "../../hooks/useCups";
import TreatmentConfigChat, {
	DraftLabels,
} from "./components/TreatmentConfigChat";
import {
	AgentConfigDraft,
	draftToPartialTemplate,
} from "./agent/draftToTemplate";
import { mergeDraftOptions, ProposedOptions } from "./agent/draftToOptions";
import {
	TreatmentPlanRequest,
	TreatmentPlan,
	TreatmentConfigTemplate,
	TreatmentConfigOptions,
	RewardEngineOption,
	TreatmentSegment,
} from "../../domain/treatmentPlan.interface";
import VisitScheduleEditor from "./visitSchedule/VisitScheduleEditor";
import PlanModeSelector, {
	AuthoringMode,
	PlanMode,
} from "./components/PlanModeSelector";
import FieldDefinitionsEditor from "./components/FieldDefinitionsEditor";
import SmartProgramConfig from "./components/SmartProgramConfig";
import { GenerateSmartProgramResponse } from "../../services/treatmentPlan.service";
import {
	SmartProgramWorkflow,
	FieldDefinition,
} from "../../domain/treatmentPlan.interface";
import { Event } from "../../domain/event.interface";
// SIMS-9026: resolving the stored option into checkbox state lives in one tested place (FR-2, FR-3).
import { resolvePropagationOption } from "./propagationOption";
import ThresholdPropagationModal from "../../components/modals/ThresholdPropagation";
import { PropagationForecast } from "../../components/modals/ThresholdPropagation/acknowledgment";
import { ForecastDisenrollment } from "../../components/modals/ThresholdPropagation";
import { findEffectiveThresholdChange } from "./thresholdChange";
import PropagationBanner from "./PropagationBanner";
import ThresholdHistoryDialog from "./ThresholdHistoryDialog";
import { treatmentPlanService } from "../../services/treatmentPlan.service";
import BehaviorSelector from "../../components/BehaviorSelector";
import TreatmentConfigFields from "../../components/TreatmentConfigFields";
import {
	collectPlanCupIds,
	inactiveCupMessage,
} from "../../components/TreatmentConfigFields/cupChoices";
import SegmentEditor from "../../components/SegmentEditor";
import {
	contiguityErrorMessage,
	readPlanSegments,
	validateSegmentOverrides,
	validateSegmentStructure,
} from "../../components/SegmentEditor/segmentEditor.helpers";
import {} from "../../utils/temporal.utils";
import { useUserStore } from "../../stores/user.store";

/** An empty template, as the editor starts and resets to. */
const blankTemplate = (): TreatmentConfigTemplate => ({
	name: "",
	default: false,
	segment: 0,
	range: "",
	duration: 0,
	periodLength: 1,
	weeklyVisitsPerPeriod: 0,
	cmVisitScheduleOptionIds: "",
	rewardEngineId: "",
	visitsPerPeriod: 0,
	minHoursBetweenVisits: 0,
	cmVisitSchedule: {
		cmVisitScheduleOptions: 0,
		cmVisitScheduleOptionsDays: "",
		cmVisitScheduleOptionsDaysDisplay: "",
		cutDays: [],
	},
	behavior: "",
	disenrollmentDaysThreshold: undefined,
	allowCustomProviderSite: false,
	additionalTreatmentConfigs: [],
});

const CopyToClipboardButton: FC<{ text: string }> = ({ text }) => {
	const [copied, setCopied] = useState(false);
	return (
		<Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
			<IconButton
				size="small"
				onClick={() => {
					navigator.clipboard.writeText(text);
					setCopied(true);
					setTimeout(() => setCopied(false), 2000);
				}}
			>
				{copied ? (
					<CheckIcon fontSize="small" color="success" />
				) : (
					<ContentCopyIcon fontSize="small" />
				)}
			</IconButton>
		</Tooltip>
	);
};

const SavedSmartProgramDetails: FC<{
	workflow: SmartProgramWorkflow;
	fieldDefinitions: FieldDefinition[];
	description?: string;
	generationPrompt?: string;
	previewHtml?: string;
}> = ({
	workflow,
	fieldDefinitions,
	description,
	generationPrompt,
	previewHtml,
}) => {
	const [showJson, setShowJson] = useState(false);
	const [showPrompt, setShowPrompt] = useState(false);
	const [showPreview, setShowPreview] = useState(false);

	const jsonContent = JSON.stringify({ workflow, fieldDefinitions }, null, 2);

	// Extract the auto-generated part (event catalog, instructions) by removing the user description portion
	const autoGeneratedContext =
		generationPrompt && description
			? generationPrompt.substring(
					generationPrompt.indexOf(`"${description}"`) + description.length + 3,
				)
			: "";

	return (
		<>
			<Grid item xs={12}>
				<Box sx={{ display: "flex", gap: 1 }}>
					<Button
						size="small"
						variant={showJson ? "contained" : "outlined"}
						startIcon={<CodeIcon />}
						onClick={() => setShowJson(!showJson)}
					>
						JSON
					</Button>
					{generationPrompt && (
						<Button
							size="small"
							variant={showPrompt ? "contained" : "outlined"}
							startIcon={<DescriptionIcon />}
							onClick={() => setShowPrompt(!showPrompt)}
						>
							Prompt
						</Button>
					)}
					{previewHtml && (
						<Button
							size="small"
							variant={showPreview ? "contained" : "outlined"}
							startIcon={<AutoAwesomeIcon />}
							onClick={() => setShowPreview(!showPreview)}
						>
							Preview
						</Button>
					)}
				</Box>
			</Grid>
			<Grid item xs={12}>
				<Collapse in={showJson}>
					<Paper variant="outlined" sx={{ mb: 1 }}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								px: 2,
								py: 1,
								borderBottom: "1px solid #e0e0e0",
								bgcolor: "#f5f5f5",
							}}
						>
							<Typography variant="caption" color="text.secondary">
								Workflow JSON
							</Typography>
							<CopyToClipboardButton text={jsonContent} />
						</Box>
						<Box
							component="pre"
							sx={{
								p: 2,
								m: 0,
								overflow: "auto",
								maxHeight: 400,
								fontSize: 12,
								fontFamily: "monospace",
							}}
						>
							{jsonContent}
						</Box>
					</Paper>
				</Collapse>
				<Collapse in={showPrompt}>
					{description && (
						<Paper variant="outlined" sx={{ mb: 1 }}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									px: 2,
									py: 1,
									borderBottom: "1px solid #e0e0e0",
									bgcolor: "#f5f5f5",
								}}
							>
								<Typography variant="caption" color="text.secondary">
									User Description
								</Typography>
								<CopyToClipboardButton text={description} />
							</Box>
							<Box
								component="pre"
								sx={{
									p: 2,
									m: 0,
									overflow: "auto",
									maxHeight: 200,
									fontSize: 12,
									fontFamily: "monospace",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
								}}
							>
								{description}
							</Box>
						</Paper>
					)}
					{autoGeneratedContext && (
						<Paper variant="outlined" sx={{ mb: 1, opacity: 0.7 }}>
							<Box
								sx={{
									px: 2,
									py: 1,
									borderBottom: "1px solid #e0e0e0",
									bgcolor: "#f5f5f5",
								}}
							>
								<Typography variant="caption" color="text.secondary">
									Auto-generated Context (events catalog, instructions)
								</Typography>
							</Box>
							<Box
								component="pre"
								sx={{
									p: 2,
									m: 0,
									overflow: "auto",
									maxHeight: 200,
									fontSize: 11,
									fontFamily: "monospace",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
									color: "text.secondary",
								}}
							>
								{autoGeneratedContext}
							</Box>
						</Paper>
					)}
				</Collapse>
				{previewHtml && (
					<Collapse in={showPreview}>
						<Paper
							variant="outlined"
							sx={{
								p: 2,
								mb: 1,
								overflow: "auto",
								"& > div": { maxWidth: "100%" },
								"& h1, & h2, & h3, & h4": { margin: "8px 0" },
								"& p": { margin: "4px 0" },
								"& table": {
									width: "100%",
									borderCollapse: "collapse",
									"& th, & td": {
										border: "1px solid #e0e0e0",
										padding: "8px",
										textAlign: "left",
									},
									"& th": { backgroundColor: "#f5f5f5" },
								},
							}}
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(previewHtml, {
									ALLOWED_TAGS: [
										"div",
										"span",
										"p",
										"h1",
										"h2",
										"h3",
										"h4",
										"h5",
										"h6",
										"ul",
										"ol",
										"li",
										"table",
										"thead",
										"tbody",
										"tr",
										"th",
										"td",
										"strong",
										"em",
										"br",
										"hr",
										"code",
										"pre",
									],
									ALLOWED_ATTR: ["class", "style"],
								}),
							}}
						/>
					</Collapse>
				)}
			</Grid>
		</>
	);
};

interface TreatmentPlanFormProps {
	editingPlan: TreatmentPlan | null;
	onClose: () => void;
	/** Fired only on a successful save, which `onClose` cannot distinguish. */
	onSaved?: () => void;
	/**
	 * Fired when a plan is created without the form closing, so the parent can
	 * switch this from a create into an edit of the plan that now exists.
	 */
	onPlanCreated?: (plan: TreatmentPlan) => void;
	events?: Event[];
	eventsLoading: boolean;
}

const TreatmentPlanForm: FC<TreatmentPlanFormProps> = ({
	editingPlan,
	onClose,
	onSaved,
	onPlanCreated,
	events,
	eventsLoading,
}) => {
	const { userInfo } = useUserStore();
	const defaultAccountId =
		process.env.REACT_APP_ACCOUNT_ID || "507f1f77bcf86cd799439011";

	const isEditMode = !!editingPlan && !!editingPlan._id;

	/**
	 * How this plan is being authored, for this editing session only.
	 *
	 * Not persisted and not part of the plan: the manual form and the assistant
	 * produce the same treatment config, so which one is on screen is a property
	 * of the session rather than of the plan. A saved Smart Program opens in the
	 * assistant because that is the only way it is built.
	 */
	const [nameError, setNameError] = useState<string | null>(null);
	const [isAutoSaving, setIsAutoSaving] = useState(false);

	const [authoringMode, setAuthoringMode] = useState<AuthoringMode>(
		editingPlan?.programType === "SMART_PROGRAM" ? "ASSISTANT" : "MANUAL",
	);

	/**
	 * Both assistants are keyed to a plan id — there is nothing to hold a
	 * conversation about until the plan exists — so choosing one saves the plan
	 * first and stays on the page. Picking the manual form never saves anything:
	 * that would turn an idle click into a row in the database.
	 */
	const handlePlanModeChange = async (mode: PlanMode): Promise<void> => {
		setAuthoringMode(mode.authoringMode);
		setFormData((previous) => ({ ...previous, programType: mode.programType }));

		if (mode.authoringMode !== "ASSISTANT" || isEditMode || isAutoSaving) {
			return;
		}

		setIsAutoSaving(true);
		try {
			// persistPlan reads formData, which the setState above has not applied
			// yet, so the program type is passed explicitly rather than relied on.
			const saved = await persistPlan({ programType: mode.programType });
			if (saved) onPlanCreated?.(saved);
		} finally {
			setIsAutoSaving(false);
		}
	};

	const [formData, setFormData] = useState<TreatmentPlanRequest>({
		accountId: defaultAccountId,
		name: "",
		programType: "WEEKLY_CALENDAR",
		treatmentConfig: {},
		treatmentConfigOptions: {
			treatmentConfigTemplates: [],
			visitsPerPeriod: [],
			minHoursBetweenVisits: [],
			duration: [],
			behavior: [],
			cmVisitScheduleOptions: [],
		},
		customPlanAllowed: false,
		// SIMS-8910 AC4: a new plan generates missed entries. The control maps 1:1 onto the
		// stored negative flag, so "defaults to generating" is the same statement as
		// "defaults to false" — there is no inversion anywhere that could drift.
		disableMissedEntriesGeneration: false,
		// SIMS-9026 (FR-3): a new plan starts as future-enrollments-only, which is today's
		// behaviour. "Never configured" and "future only" are deliberately the same state.
		propagateThresholdToCurrentEnrollments: false,
	});

	// SIMS-9028/9031: a threshold change on a propagating plan is confirmed before it is applied.
	// The save is held here until the administrator acknowledges the forecast (D2) — AC6: the plan
	// itself is NOT written until then either.
	const [pendingPropagation, setPendingPropagation] = useState<{
		/** AC6: the plan edit, held unsaved until the administrator confirms. */
		planData: TreatmentPlanRequest & { updatedBy?: string };
		templateName: string;
		templateId?: string;
		field: "TEMPLATE_DEFAULT" | "SEGMENT";
		segmentWeekRange?: { start: number; end: number };
		fieldLabel: string;
		previousValue: number;
		nextValue: number;
		forecast: PropagationForecast;
		/** SIMS-9032 AC1 — kept beside the counts, since the modal's forecast type is counts-only. */
		forecastDisenrolled: ForecastDisenrollment[];
		propagationInProgress: boolean;
		forecastChanged: boolean;
	} | null>(null);
	const [propagationSubmitting, setPropagationSubmitting] = useState(false);
	// SIMS-9033: bumped after a change is applied so the banner reloads immediately rather than
	// waiting for the next poll.
	const [propagationRefreshKey, setPropagationRefreshKey] = useState(0);
	/** SIMS-9024 — the threshold change history dialog (SIMS-9037/9038). */
	const [historyOpen, setHistoryOpen] = useState(false);

	const [newVisitPeriod, setNewVisitPeriod] = useState("");
	const [newMinHours, setNewMinHours] = useState("");
	const [newDuration, setNewDuration] = useState("");

	// Template creation state
	const [showTemplateForm, setShowTemplateForm] = useState(false);
	const [editingTemplateIndex, setEditingTemplateIndex] = useState<
		number | null
	>(null);
	const [templateData, setTemplateData] =
		useState<TreatmentConfigTemplate>(blankTemplate);

	// State for adding new cut day patterns

	const { data: rewardEngines } = useRewardsEngine(true);
	const { data: cupsData } = useCups();
	// SIMS-8831 AC3 — from the SAVED plan, not the draft: a cup unticked by mistake stays a choice
	// until the plan is saved without it.
	const retainedCupIds = useMemo(
		() => collectPlanCupIds(editingPlan),
		[editingPlan],
	);
	const [saveError, setSaveError] = useState<string | null>(null);
	const createMutation = useCreateTreatmentPlan();
	const updateMutation = useUpdateTreatmentPlan();

	// SIMS-7151: reward engine id -> display name options, reused by the segment editor's
	// collapsed header (mirrors the per-template mapping below).
	const rewardEngineOptions: RewardEngineOption[] = useMemo(
		() =>
			rewardEngines
				?.filter((engine) => engine._id)
				.map((engine) => ({
					id: engine._id as string,
					name: `${engine.eventType} - ${engine.event}`,
				})) || [],
		[rewardEngines],
	);

	/**
	 * A saved plan's own type wins over the form's.
	 *
	 * `formData` starts on the WEEKLY_CALENDAR default and is seeded from the
	 * plan afterwards, so for one render an existing Smart Program looks like a
	 * weekly plan — long enough to mount the wrong assistant.
	 */
	const resolvedProgramType = editingPlan?.programType ?? formData.programType;

	/**
	 * The assistant's draft names a behaviour and an engine by id. Reusing the
	 * same option lists the manual form shows means the two cannot disagree
	 * about what an id is called.
	 */
	const agentLabels: DraftLabels = useMemo(
		() => ({
			behaviors: Object.fromEntries(
				(events ?? [])
					.filter((event) => event._id)
					.map((event) => [event._id as string, event.name]),
			),
			engines: Object.fromEntries(
				rewardEngineOptions.map((engine) => [engine.id, engine.name]),
			),
		}),
		[events, rewardEngineOptions],
	);

	/**
	 * Append a config the assistant produced to the plan's templates.
	 *
	 * Nothing is persisted here: the template lands in the form exactly as if it
	 * had been filled in by hand, and the clinician saves the plan. An assistant
	 * that wrote to the database directly would be a config nobody reviewed.
	 */
	/**
	 * Fold options the assistant has proposed into the plan as it proposes them.
	 *
	 * Safe to run on every turn because the merge only ever adds: a value the
	 * plan already offers is left alone. Returning `previous` unchanged when
	 * there is nothing new is what stops this looping — the state object has to
	 * be identical, not merely equal.
	 */
	const applyAgentOptions = useCallback((proposed: ProposedOptions): void => {
		setFormData((previous) => {
			const { options, additions } = mergeDraftOptions(
				proposed,
				previous.treatmentConfigOptions,
			);
			if (additions.length === 0) return previous;
			return {
				...previous,
				treatmentConfigOptions: {
					...options,
					treatmentConfigTemplates:
						previous.treatmentConfigOptions?.treatmentConfigTemplates ?? [],
				},
			};
		});
		// Reads nothing from the render — the merge happens against the state
		// updater's `previous`, so this identity can stay stable and the
		// consumer's effect fires only when the draft actually changes.
	}, []);

	/**
	 * Leave the conversation and finish the config by hand.
	 *
	 * The captured half seeds the manual editor rather than being saved as a
	 * template: a partial template in the array would be a broken one.
	 */
	const continueAgentDraftManually = (draft: AgentConfigDraft | null): void => {
		setTemplateData(
			draftToPartialTemplate(
				draft,
				formData.treatmentConfigOptions,
				blankTemplate(),
			),
		);
		setEditingTemplateIndex(null);
		setShowTemplateForm(true);
		setAuthoringMode("MANUAL");
	};

	const applyAgentDraft = ({
		options,
		template,
	}: {
		options: TreatmentConfigOptions;
		template: TreatmentConfigTemplate;
	}): void => {
		setFormData((previous) => {
			// `options` already carries the plan's lists with the assistant's
			// proposals folded in, but the templates on it came from the render
			// this was computed in — take those from `previous` so a template
			// added meanwhile is not dropped.
			const templates =
				previous.treatmentConfigOptions?.treatmentConfigTemplates ?? [];
			return {
				...previous,
				treatmentConfigOptions: {
					...options,
					treatmentConfigTemplates: [...templates, template],
				},
			};
		});
	};

	// SIMS-7151/7159: the plan's default config template supplies the effective values a segment
	// inherits when its override toggle is OFF (reward engine, behavior, visit schedule,
	// disenrollment threshold).
	const defaultTemplate =
		formData.treatmentConfigOptions?.treatmentConfigTemplates?.find(
			(t) => t.default,
		) || formData.treatmentConfigOptions?.treatmentConfigTemplates?.[0];
	const segmentTemplateDefaults = {
		rewardEngineId: defaultTemplate?.rewardEngineId,
		behavior: defaultTemplate?.behavior,
		cmVisitScheduleOptionIds: defaultTemplate?.cmVisitScheduleOptionIds,
		disenrollmentDaysThreshold: defaultTemplate?.disenrollmentDaysThreshold,
	};

	// Load editing plan data
	useEffect(() => {
		if (editingPlan) {
			setFormData({
				accountId: editingPlan.accountId,
				name: editingPlan.name || "",
				programType: editingPlan.programType || "WEEKLY_CALENDAR",
				treatmentConfig: editingPlan.treatmentConfig || {},
				treatmentConfigOptions: editingPlan.treatmentConfigOptions || {
					treatmentConfigTemplates: [],
					visitsPerPeriod: [],
					minHoursBetweenVisits: [],
					duration: [],
					behavior: [],
					cmVisitScheduleOptions: [],
				},
				smartProgram: editingPlan.smartProgram,
				customPlanAllowed: editingPlan.customPlanAllowed || false,
				// SIMS-8910 AC1/AC2: reopening the plan shows the saved state. A plan saved
				// before this release has no value here and reads as generating.
				disableMissedEntriesGeneration:
					editingPlan.disableMissedEntriesGeneration ?? false,
				// SIMS-9026 (AC2/AC3): a plan saved before this feature has no stored value, so
				// `|| false` resolves it to future-enrollments-only — no backfill (FR-3). A plan
				// that DID set it round-trips its choice back into the form (AC3).
				propagateThresholdToCurrentEnrollments:
					resolvePropagationOption(editingPlan),
				// SIMS-7151: pre-populate segments from the folded read location
				// (treatmentConfig.segments); absent/empty yields [] (AC3/AC4).
				segments: readPlanSegments(editingPlan.treatmentConfig),
			});
		}
	}, [editingPlan]);

	/**
	 * Save the plan and report what was saved, WITHOUT closing the form.
	 *
	 * Split out from `handleSubmit` because choosing an assistant needs the plan
	 * to exist while the user stays on the page: the conversation is keyed to a
	 * plan id, so there is nothing to talk about until one exists.
	 *
	 * Returns null when a validation gate stopped the save, so callers can tell
	 * "refused" from "saved".
	 */
	const persistPlan = async (
		overrides: Partial<TreatmentPlanRequest> = {},
	): Promise<TreatmentPlan | null> => {
		if (!formData.name.trim()) {
			setNameError("A treatment plan needs a name.");
			return null;
		}

		try {
			// Clean the payload before sending to API
			const cleanedFormData = { ...formData, ...overrides };

			// Remove frontend-only fields from cmVisitScheduleOptions
			if (cleanedFormData.treatmentConfigOptions?.cmVisitScheduleOptions) {
				cleanedFormData.treatmentConfigOptions.cmVisitScheduleOptions =
					cleanedFormData.treatmentConfigOptions.cmVisitScheduleOptions.map(
						(option) => {
							const cleanOption = { ...option };
							if ("selectedWeekdays" in cleanOption) {
								delete (cleanOption as Record<string, unknown>)
									.selectedWeekdays;
							}
							if ("selectedCutDays" in cleanOption) {
								delete (cleanOption as Record<string, unknown>).selectedCutDays;
							}
							return cleanOption;
						},
					);
			}

			// Remove frontend-only fields from cmVisitSchedule in templates
			if (cleanedFormData.treatmentConfigOptions?.treatmentConfigTemplates) {
				cleanedFormData.treatmentConfigOptions.treatmentConfigTemplates =
					cleanedFormData.treatmentConfigOptions.treatmentConfigTemplates.map(
						(template) => {
							const cleanTemplate = { ...template };
							if (cleanTemplate.cmVisitSchedule) {
								const cleanSchedule = { ...cleanTemplate.cmVisitSchedule };
								if ("selectedWeekdays" in cleanSchedule) {
									delete (cleanSchedule as Record<string, unknown>)
										.selectedWeekdays;
								}
								if ("selectedCutDays" in cleanSchedule) {
									delete (cleanSchedule as Record<string, unknown>)
										.selectedCutDays;
								}
								cleanTemplate.cmVisitSchedule = cleanSchedule;
							}
							return cleanTemplate;
						},
					);
			}

			// SIMS-8910 AC3 (C17850): a Smart Program plan must carry no missed-entry
			// generation value at all. The control is not rendered for that program type, but
			// the field still sits in form state at its default — and switching Program Type
			// mid-edit (C17849) can leave a value the user set while the form was weekly.
			// Dropping it here is what makes "not offered" and "not stored" the same thing,
			// rather than relying on the default happening to be harmless.
			if (cleanedFormData.programType === "SMART_PROGRAM") {
				delete cleanedFormData.disableMissedEntriesGeneration;
			}

			// Validate templates
			if (cleanedFormData.treatmentConfigOptions?.treatmentConfigTemplates) {
				const invalidTemplates =
					cleanedFormData.treatmentConfigOptions.treatmentConfigTemplates.filter(
						(template) =>
							!template.name.trim() ||
							!template.range.trim() ||
							template.segment <= 0 ||
							template.duration < 0 ||
							!template.weeklyVisitsPerPeriod ||
							!template.rewardEngineId.trim() ||
							!template.cmVisitScheduleOptionIds.trim() ||
							!template.cmVisitSchedule.cmVisitScheduleOptionsDays.trim() ||
							!template.cmVisitSchedule.cmVisitScheduleOptionsDaysDisplay.trim() ||
							!template.behavior.trim(),
					);

				if (invalidTemplates.length > 0) {
					alert(
						"Please fill all required template fields before saving the treatment plan.",
					);
					return null;
				}
			}

			// SIMS-7151 (AC1): the structural fields must be usable. Checked FIRST — a segment with
			// no visits per week makes every later rule meaningless (it occupies zero visits, so it
			// silently never applies at runtime).
			const structureError = validateSegmentStructure(formData.segments || []);
			if (structureError) {
				alert(structureError);
				return null;
			}

			// SIMS-7159 (AC2): a segment override toggled ON but left empty/invalid blocks save.
			const overrideError = validateSegmentOverrides(formData.segments || []);
			if (overrideError) {
				alert(overrideError);
				return null;
			}

			// SIMS-7167 (AC2/AC3/AC5): non-contiguous / overlapping / not-starting-at-1 segment
			// week ranges block save (mirrors the server-side rule so the UI catches it first).
			const contiguityError = contiguityErrorMessage(formData.segments || []);
			if (contiguityError) {
				alert(contiguityError);
				return null;
			}

			if (isEditMode && editingPlan) {
				// SIMS-9027 (FR-5/FR-16): propagation is offered ONLY when the plan opted in AND the
				// EFFECTIVE threshold actually moved. Re-saving the same value, or editing anything
				// else, must never disturb a running cohort.
				const thresholdChange = findEffectiveThresholdChange(
					editingPlan,
					cleanedFormData as TreatmentPlanRequest,
				);

				if (thresholdChange) {
					// SIMS-9031 AC6: NOTHING is persisted until the administrator confirms. The plan
					// used to be saved here, before the confirmation, so cancelling left it holding a
					// threshold the administrator had abandoned while participants kept the old one —
					// the drift that makes a later change look like a per-participant exception.
					//
					// The preview does not need the saved plan: it is given the prospective values
					// explicitly, and derives the cohort from the ENROLLMENT snapshots.
					// The preview must have its OWN catch. Without one a rejection unwound to the
					// generic handler below, which only console.errors — so the administrator clicked
					// Save and got no modal, no error and no saved plan: a dead button. The preview
					// rejects on ordinary conditions, not just infrastructure faults (a 4xx from the
					// DTO validations, or from the FR-19 opt-in guard), so this path is reachable in
					// normal use and must say something.
					let forecast: Awaited<
						ReturnType<typeof treatmentPlanService.previewThresholdPropagation>
					>;
					try {
						forecast = await treatmentPlanService.previewThresholdPropagation(
							editingPlan._id,
							{
								templateName: thresholdChange.templateName,
								templateId: thresholdChange.templateId,
								// FR-11: the preview must count what apply() will really do, so it
								// needs the same field/segment the apply call gets.
								field: thresholdChange.field,
								segmentWeekRange: thresholdChange.segmentWeekRange,
								nextThreshold: thresholdChange.nextValue,
								templateThresholdBeforeSave: thresholdChange.previousValue,
							},
						);
					} catch (err) {
						// eslint-disable-next-line no-console
						console.error("Could not preview the threshold change:", err);
						// AC6 still holds: nothing was persisted, so there is nothing to undo. The
						// form stays open and dirty so the change is not lost.
						alert(
							"Could not work out who this threshold change would affect, so it has not " +
								"been applied and the plan has not been saved. Try again, or untick " +
								"the option to apply threshold changes to current enrollments to save " +
								"the plan for future enrollments only.",
						);
						// null, not undefined: development's persistPlan is typed
						// Promise<TreatmentPlan | null>. Falsy either way, so handleSubmit still
						// leaves the form open — AC6 is unchanged.
						return null;
					}

					setPendingPropagation({
						forecastDisenrolled: forecast.forecastDisenrolled ?? [],
						// AC6: held rather than saved. Confirming saves exactly what was previewed,
						// so the plan and the cohort cannot end up describing different changes.
						planData: { ...cleanedFormData, updatedBy: userInfo?.id },
						templateName: thresholdChange.templateName,
						templateId: thresholdChange.templateId,
						field: thresholdChange.field,
						segmentWeekRange: thresholdChange.segmentWeekRange,
						// FR-8: name the field, so "21 -> 45" is readable as an event later.
						fieldLabel:
							thresholdChange.field === "SEGMENT"
								? `segment ${thresholdChange.segmentNumber} (weeks ${thresholdChange.segmentWeekRange?.start}-${thresholdChange.segmentWeekRange?.end})`
								: "the plan default",
						previousValue: thresholdChange.previousValue,
						nextValue: thresholdChange.nextValue,
						forecast,
						propagationInProgress: forecast.propagationInProgress,
						forecastChanged: false,
					});
					// Hold the form open until the administrator decides. AC6: cancelling
					// persists nothing at all and leaves the form dirty, so the administrator can
					// untick propagation and save the plan change on its own.
					//
					// null rather than a saved plan: persistPlan's caller treats a falsy result as
					// "not saved, stay open", which is exactly the state the confirmation needs.
					return null;
				}

				// `return await` per development's persistPlan contract: callers need the saved
				// plan back (choosing an assistant saves first, then keys the conversation to the
				// plan id). The propagation branch above deliberately returns undefined instead —
				// falsy, so handleSubmit leaves the form open for the confirmation (AC6).
				return await updateMutation.mutateAsync({
					id: editingPlan._id,
					data: { ...cleanedFormData, updatedBy: userInfo?.id },
				});
			}

			return await createMutation.mutateAsync({
				...cleanedFormData,
				createdBy: userInfo?.id,
			});
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error("Error saving treatment plan:", err);
			setSaveError(inactiveCupMessage(err));
			return null;
		}
	};

	const handleSubmit = async (): Promise<void> => {
		setSaveError(null);
		const saved = await persistPlan();
		if (!saved) return;
		onSaved?.();
		onClose();
	};

	const addArrayItem = (
		field: keyof NonNullable<typeof formData.treatmentConfigOptions>,
		value: string,
		setter: (value: string) => void,
	) => {
		if (value.trim() && formData.treatmentConfigOptions) {
			const currentArray =
				(formData.treatmentConfigOptions[field] as string[]) || [];
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					[field]: [...currentArray, value.trim()],
				},
			});
			setter("");
		}
	};

	const removeArrayItem = (
		field: keyof NonNullable<typeof formData.treatmentConfigOptions>,
		index: number,
	) => {
		if (formData.treatmentConfigOptions) {
			const currentArray =
				(formData.treatmentConfigOptions[field] as string[]) || [];
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					[field]: currentArray.filter((_, i) => i !== index),
				},
			});
		}
	};

	const addTemplate = () => {
		if (!formData.treatmentConfigOptions) {
			setFormData({
				...formData,
				treatmentConfigOptions: {
					treatmentConfigTemplates: [templateData],
					visitsPerPeriod: [],
					minHoursBetweenVisits: [],
					duration: [],
					behavior: [],
				},
			});
		} else {
			const currentTemplates =
				formData.treatmentConfigOptions.treatmentConfigTemplates || [];

			// Check if we're editing an existing template
			if (editingTemplateIndex !== null) {
				// Update existing template
				const updatedTemplates = [...currentTemplates];
				// SIMS-9041: carry the template's stable `_id` across the edit. The editor holds its
				// own local copy of the template, so replacing the array entry with that copy drops
				// any field the editor does not model — and `_id` is one. Losing it re-mints a new
				// id on save, which orphans every enrollment pointing at the old one: their cups,
				// analytes and fishbowl config stop resolving and their visit-entry form silently
				// stops collecting them. Verified before the fix: a no-change save stripped a
				// planted id.
				const existingId = (
					currentTemplates[editingTemplateIndex] as { _id?: string }
				)?._id;
				updatedTemplates[editingTemplateIndex] = existingId
					? ({ ...templateData, _id: existingId } as typeof templateData)
					: templateData;
				setFormData({
					...formData,
					treatmentConfigOptions: {
						...formData.treatmentConfigOptions,
						treatmentConfigTemplates: updatedTemplates,
					},
				});
			} else {
				// Add new template
				setFormData({
					...formData,
					treatmentConfigOptions: {
						...formData.treatmentConfigOptions,
						treatmentConfigTemplates: [...currentTemplates, templateData],
					},
				});
			}
		}

		// Reset form
		setTemplateData({
			name: "",
			default: false,
			segment: 0,
			range: "",
			duration: 0,
			periodLength: 1,
			weeklyVisitsPerPeriod: 0,
			cmVisitScheduleOptionIds: "",
			rewardEngineId: "",
			visitsPerPeriod: 0,
			minHoursBetweenVisits: 0,
			cmVisitSchedule: {
				cmVisitScheduleOptions: 0,
				cmVisitScheduleOptionsDays: "",
				cmVisitScheduleOptionsDaysDisplay: "",
				cutDays: [],
			},
			behavior: "",
			disenrollmentDaysThreshold: undefined,
			allowCustomProviderSite: false,
			additionalTreatmentConfigs: [],
		});
		setShowTemplateForm(false);
		setEditingTemplateIndex(null);
	};

	const removeTemplate = (index: number) => {
		if (formData.treatmentConfigOptions?.treatmentConfigTemplates) {
			const currentTemplates =
				formData.treatmentConfigOptions.treatmentConfigTemplates;
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					treatmentConfigTemplates: currentTemplates.filter(
						(_, i) => i !== index,
					),
				},
			});
		}
	};

	const editTemplate = (index: number) => {
		if (formData.treatmentConfigOptions?.treatmentConfigTemplates) {
			const template =
				formData.treatmentConfigOptions.treatmentConfigTemplates[index];
			setTemplateData(template);
			setEditingTemplateIndex(index);
			setShowTemplateForm(true);
		}
	};

	return (
		<Card sx={{ mb: 3 }}>
			<CardContent>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
					}}
				>
					<Typography variant="h6">
						{isEditMode
							? "Edit Treatment Plan"
							: editingPlan
								? "Clone Treatment Plan"
								: "Create New Treatment Plan"}
					</Typography>
					<Button variant="outlined" onClick={onClose}>
						Cancel
					</Button>
				</Box>

				{/* SIMS-9033 (AC2/AC3/AC4): the state of any threshold change applied to current
				    participants. Edit mode only — a plan being created has no propagation history,
				    and none can be running for it. */}
				{isEditMode && editingPlan && (
					<PropagationBanner
						treatmentPlanId={editingPlan._id}
						refreshKey={propagationRefreshKey}
					/>
				)}

				{/* SIMS-9024 — opened from the button beside the propagation option. Mounted here
				    so it is a sibling of the form rather than nested inside a Grid item. */}
				{isEditMode && editingPlan && (
					<ThresholdHistoryDialog
						open={historyOpen}
						onClose={() => setHistoryOpen(false)}
						treatmentPlanId={editingPlan._id}
						planName={editingPlan.name}
					/>
				)}

				<Grid container spacing={3}>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Account ID"
							value={formData.accountId}
							onChange={(e) =>
								setFormData({
									...formData,
									accountId: e.target.value,
								})
							}
							placeholder="Enter account ID"
							helperText="Default account ID is pre-filled from environment"
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Treatment Plan Name"
							value={formData.name}
							onChange={(e) => {
								setNameError(null);
								setFormData({
									...formData,
									name: e.target.value,
								});
							}}
							placeholder="Enter treatment plan name"
							required
							error={!!nameError}
							helperText={
								nameError ??
								"A descriptive name to identify this treatment plan"
							}
						/>
					</Grid>

					<Grid item xs={12}>
						<PlanModeSelector
							value={{
								programType: formData.programType || "WEEKLY_CALENDAR",
								authoringMode,
							}}
							onChange={handlePlanModeChange}
							// Program type is fixed once the plan exists; authoring mode
							// never is, so this locks the type rather than the whole control.
							lockedProgramType={
								isEditMode ? editingPlan?.programType || null : null
							}
						/>
					</Grid>

					{/* The treatment-config assistant. Same output as the manual form
					    below, which stays on screen: the assistant fills the template
					    in, and the clinician reviews and saves it there. */}
					{authoringMode === "ASSISTANT" &&
						resolvedProgramType !== "SMART_PROGRAM" && (
							<Grid item xs={12}>
								{isEditMode && editingPlan ? (
									<TreatmentConfigChat
										treatmentPlanId={editingPlan._id}
										planOptions={formData.treatmentConfigOptions}
										labels={agentLabels}
										onApply={applyAgentDraft}
										onOptionsProposed={applyAgentOptions}
										onContinueManually={continueAgentDraftManually}
									/>
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{
											p: 2,
											textAlign: "center",
											border: "1px dashed #ccc",
											borderRadius: 1,
										}}
									>
										{isAutoSaving
											? "Saving the plan so the assistant has something to work on…"
											: "Give the plan a name to start the assistant."}
									</Typography>
								)}
							</Grid>
						)}

					{formData.programType === "SMART_PROGRAM" && (
						<>
							<Grid item xs={12}>
								{isEditMode && editingPlan ? (
									<SmartProgramConfig
										treatmentPlanId={editingPlan._id}
										onApprove={(result: GenerateSmartProgramResponse) => {
											setFormData({
												...formData,
												smartProgram: {
													workflow:
														result.workflow as unknown as SmartProgramWorkflow,
													fieldDefinitions:
														result.fieldDefinitions as FieldDefinition[],
													description: result.description,
													generationPrompt: result.prompt,
													previewHtml: result.previewHtml,
												},
											});
										}}
										onCancel={() => {
											/* no-op */
										}}
									/>
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{
											p: 2,
											textAlign: "center",
											border: "1px dashed #ccc",
											borderRadius: 1,
										}}
									>
										Save the treatment plan first, then use AI generation to
										configure the Smart Program workflow.
									</Typography>
								)}
							</Grid>
							{formData.smartProgram?.workflow && (
								<SavedSmartProgramDetails
									workflow={formData.smartProgram.workflow}
									fieldDefinitions={formData.smartProgram.fieldDefinitions}
									description={formData.smartProgram.description}
									generationPrompt={formData.smartProgram.generationPrompt}
									previewHtml={formData.smartProgram.previewHtml}
								/>
							)}
							<Grid item xs={12}>
								<FieldDefinitionsEditor
									fields={formData.smartProgram?.fieldDefinitions || []}
									onChange={(fields) =>
										setFormData({
											...formData,
											smartProgram: {
												...formData.smartProgram,
												fieldDefinitions: fields,
											},
										})
									}
								/>
							</Grid>
						</>
					)}

					{/* Treatment Config Templates (Weekly Calendar only) */}
					{(!formData.programType ||
						formData.programType === "WEEKLY_CALENDAR") && (
						<>
							{/* Treatment Config Templates */}
							<Grid item xs={12}>
								<Box
									sx={{
										border: "1px solid #e0e0e0",
										borderRadius: 1,
										p: 2,
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 2,
										}}
									>
										<Typography variant="subtitle1">
											Treatment Config Templates
										</Typography>
										<Button
											variant="outlined"
											size="small"
											onClick={() => {
												if (showTemplateForm) {
													// Cancel - close form and reset
													setShowTemplateForm(false);
													setEditingTemplateIndex(null);
													setTemplateData({
														name: "",
														default: false,
														segment: 0,
														range: "",
														duration: 0,
														periodLength: 1,
														weeklyVisitsPerPeriod: 0,
														cmVisitScheduleOptionIds: "",
														rewardEngineId: "",
														visitsPerPeriod: 0,
														minHoursBetweenVisits: 0,
														cmVisitSchedule: {
															cmVisitScheduleOptions: 0,
															cmVisitScheduleOptionsDays: "",
															cmVisitScheduleOptionsDaysDisplay: "",
															cutDays: [],
														},
														behavior: "",
														disenrollmentDaysThreshold: undefined,
														allowCustomProviderSite: false,
													});
												} else {
													// Open form in create mode
													setEditingTemplateIndex(null);
													setShowTemplateForm(true);
												}
											}}
										>
											{showTemplateForm ? "Cancel" : "Add Template"}
										</Button>
									</Box>

									{/* Show existing templates */}
									{formData.treatmentConfigOptions?.treatmentConfigTemplates &&
										formData.treatmentConfigOptions.treatmentConfigTemplates
											.length > 0 && (
											<Box sx={{ mb: 2 }}>
												<Typography
													variant="caption"
													display="block"
													sx={{ mb: 1 }}
												>
													Existing Templates:
												</Typography>
												<Box
													sx={{
														display: "flex",
														flexWrap: "wrap",
														gap: 1,
													}}
												>
													{formData.treatmentConfigOptions.treatmentConfigTemplates.map(
														(template, index) => (
															<Box
																key={index}
																sx={{
																	display: "flex",
																	alignItems: "center",
																	border: "1px solid #e0e0e0",
																	borderRadius: 1,
																	px: 1,
																	py: 0.5,
																}}
															>
																<Typography
																	variant="body2"
																	sx={{ mr: 1, fontWeight: 500 }}
																>
																	{template.name || `Template ${index + 1}`}
																</Typography>
																<IconButton
																	size="small"
																	onClick={() => editTemplate(index)}
																	title="Edit template"
																	sx={{ p: 0.5 }}
																>
																	<EditIcon fontSize="small" />
																</IconButton>
																<IconButton
																	size="small"
																	onClick={() => removeTemplate(index)}
																	title="Delete template"
																	sx={{ p: 0.5 }}
																	color="error"
																>
																	<DeleteIcon fontSize="small" />
																</IconButton>
															</Box>
														),
													)}
												</Box>
											</Box>
										)}

									{/* Template creation form */}
									{showTemplateForm && (
										<Card variant="outlined" sx={{ p: 2 }}>
											<Typography variant="subtitle2" sx={{ mb: 2 }}>
												{editingTemplateIndex !== null
													? "Edit Template"
													: "Create New Template"}
											</Typography>

											<Grid container spacing={2}>
												<Grid item xs={12}>
													{/* Use the reusable TreatmentConfigFields component */}
													<TreatmentConfigFields
														config={templateData}
														onChange={(updatedConfig) =>
															setTemplateData(updatedConfig)
														}
														options={{
															behaviors: events || [],
															durations:
																formData.treatmentConfigOptions?.duration?.map(
																	String,
																) || [],
															visitsPerPeriod:
																formData.treatmentConfigOptions?.visitsPerPeriod?.map(
																	String,
																) || [],
															minHoursBetweenVisits:
																formData.treatmentConfigOptions?.minHoursBetweenVisits?.map(
																	String,
																) || [],
															cmVisitScheduleOptions:
																formData.treatmentConfigOptions
																	?.cmVisitScheduleOptions || [],
															rewardEngineOptions:
																rewardEngines
																	?.filter((engine) => engine._id)
																	.map((engine) => ({
																		id: engine._id as string,
																		name: `${engine.eventType} - ${engine.event}`,
																	})) || [],
															cups: cupsData?.body || [],
														}}
														retainedCupIds={retainedCupIds}
														showDefaultCheckbox={true}
														showAllowCustomProviderSite={true}
													/>
												</Grid>

												{/* Additional Treatment Configs Section */}
												<Grid item xs={12}>
													<Box
														sx={{
															border: "1px solid #e0e0e0",
															borderRadius: 1,
															p: 2,
															mt: 2,
														}}
													>
														<Box
															sx={{
																display: "flex",
																justifyContent: "space-between",
																alignItems: "center",
																mb: 2,
															}}
														>
															<Typography variant="subtitle2">
																Additional Activities
															</Typography>
															<Typography
																variant="caption"
																color="text.secondary"
															>
																Add more activities beyond the preferred one
															</Typography>
														</Box>

														{/* List of Additional Configs */}
														{templateData.additionalTreatmentConfigs &&
															templateData.additionalTreatmentConfigs.length >
																0 && (
																<Box sx={{ mb: 2 }}>
																	{templateData.additionalTreatmentConfigs.map(
																		(config, index) => (
																			<Paper
																				key={index}
																				sx={{
																					p: 2,
																					mb: 1,
																					display: "flex",
																					justifyContent: "space-between",
																					alignItems: "center",
																				}}
																			>
																				<Box>
																					<Typography
																						variant="body2"
																						fontWeight="bold"
																					>
																						{config.name}
																					</Typography>
																					<Typography
																						variant="caption"
																						color="text.secondary"
																					>
																						Segment: {config.segment}, Duration:{" "}
																						{config.duration}, Visits/Period:{" "}
																						{config.visitsPerPeriod}
																					</Typography>
																				</Box>
																				<Box>
																					<IconButton
																						size="small"
																						onClick={() => {
																							// Remove this additional config
																							const updatedConfigs = [
																								...(templateData.additionalTreatmentConfigs ||
																									[]),
																							];
																							updatedConfigs.splice(index, 1);
																							setTemplateData({
																								...templateData,
																								additionalTreatmentConfigs:
																									updatedConfigs,
																							});
																						}}
																						color="error"
																					>
																						<DeleteIcon />
																					</IconButton>
																				</Box>
																			</Paper>
																		),
																	)}
																</Box>
															)}

														{/* Add Additional Config Form */}
														<Button
															variant="outlined"
															size="small"
															startIcon={<AddIcon />}
															onClick={() => {
																// Create a simplified config based on current template
																const newAdditionalConfig: TreatmentConfigTemplate =
																	{
																		name: `${templateData.name} - Additional Activity ${(templateData.additionalTreatmentConfigs?.length || 0) + 1}`,
																		default: false,
																		segment: templateData.segment,
																		range: templateData.range,
																		duration: templateData.duration,
																		periodLength: templateData.periodLength,
																		weeklyVisitsPerPeriod:
																			templateData.weeklyVisitsPerPeriod,
																		cmVisitScheduleOptionIds:
																			templateData.cmVisitScheduleOptionIds,
																		rewardEngineId: templateData.rewardEngineId,
																		visitsPerPeriod:
																			templateData.visitsPerPeriod,
																		minHoursBetweenVisits:
																			templateData.minHoursBetweenVisits,
																		cmVisitSchedule: {
																			...templateData.cmVisitSchedule,
																		},
																		behavior: "", // Empty behavior to be selected
																		disenrollmentDaysThreshold:
																			templateData.disenrollmentDaysThreshold,
																		allowCustomProviderSite:
																			templateData.allowCustomProviderSite,
																		additionalTreatmentConfigs: [], // Nested configs not supported
																	};

																setTemplateData({
																	...templateData,
																	additionalTreatmentConfigs: [
																		...(templateData.additionalTreatmentConfigs ||
																			[]),
																		newAdditionalConfig,
																	],
																});
															}}
														>
															Add Additional Activity
														</Button>

														{/* Edit forms for each additional config */}
														{templateData.additionalTreatmentConfigs &&
															templateData.additionalTreatmentConfigs.length >
																0 && (
																<Box sx={{ mt: 2 }}>
																	{templateData.additionalTreatmentConfigs.map(
																		(config, index) => {
																			const updateConfig = (
																				updates: Partial<TreatmentConfigTemplate>,
																			) => {
																				const updatedConfigs = [
																					...(templateData.additionalTreatmentConfigs ||
																						[]),
																				];
																				updatedConfigs[index] = {
																					...updatedConfigs[index],
																					...updates,
																				};
																				setTemplateData({
																					...templateData,
																					additionalTreatmentConfigs:
																						updatedConfigs,
																				});
																			};

																			return (
																				<Card
																					key={index}
																					variant="outlined"
																					sx={{
																						p: 2,
																						mb: 2,
																						bgcolor: "grey.50",
																					}}
																				>
																					<Box
																						sx={{
																							display: "flex",
																							justifyContent: "space-between",
																							alignItems: "center",
																							mb: 2,
																						}}
																					>
																						<Typography variant="subtitle2">
																							Additional Activity {index + 1}
																						</Typography>
																						<IconButton
																							size="small"
																							onClick={() => {
																								const updatedConfigs = [
																									...(templateData.additionalTreatmentConfigs ||
																										[]),
																								];
																								updatedConfigs.splice(index, 1);
																								setTemplateData({
																									...templateData,
																									additionalTreatmentConfigs:
																										updatedConfigs,
																								});
																							}}
																							color="error"
																						>
																							<DeleteIcon />
																						</IconButton>
																					</Box>

																					{/* Use the reusable TreatmentConfigFields component */}
																					<TreatmentConfigFields
																						config={config}
																						onChange={(updatedConfig) =>
																							updateConfig(updatedConfig)
																						}
																						options={{
																							behaviors: events || [],
																							durations:
																								formData.treatmentConfigOptions?.duration?.map(
																									String,
																								) || [],
																							visitsPerPeriod:
																								formData.treatmentConfigOptions?.visitsPerPeriod?.map(
																									String,
																								) || [],
																							minHoursBetweenVisits:
																								formData.treatmentConfigOptions?.minHoursBetweenVisits?.map(
																									String,
																								) || [],
																							cmVisitScheduleOptions:
																								formData.treatmentConfigOptions
																									?.cmVisitScheduleOptions ||
																								[],
																							rewardEngineOptions:
																								rewardEngines
																									?.filter(
																										(engine) => engine._id,
																									)
																									.map((engine) => ({
																										id: engine._id as string,
																										name: `${engine.eventType} - ${engine.event}`,
																									})) || [],
																							cups: cupsData?.body || [],
																						}}
																						retainedCupIds={retainedCupIds}
																						showDefaultCheckbox={false}
																						showAllowCustomProviderSite={true}
																						isAdditionalConfig={true}
																					/>
																				</Card>
																			);
																		},
																	)}
																</Box>
															)}
													</Box>
												</Grid>

												<Grid item xs={12}>
													<Button
														variant="contained"
														onClick={addTemplate}
														disabled={!templateData.name.trim()}
													>
														{editingTemplateIndex !== null
															? "Update Template"
															: "Add Template"}
													</Button>
												</Grid>
											</Grid>
										</Card>
									)}
								</Box>
							</Grid>

							{/* Visit schedules, and the periods each check counts. */}
							<Grid item xs={12}>
								<Box
									sx={{
										border: "1px solid #e0e0e0",
										borderRadius: 1,
										p: 2,
									}}
								>
									<VisitScheduleEditor
										options={
											formData.treatmentConfigOptions?.cmVisitScheduleOptions ??
											[]
										}
										onChange={(cmVisitScheduleOptions) =>
											setFormData((previous) => ({
												...previous,
												treatmentConfigOptions: {
													...previous.treatmentConfigOptions,
													cmVisitScheduleOptions,
												},
											}))
										}
									/>
								</Box>
							</Grid>

							{/* Visits Per Period */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Visits Per Period
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										size="small"
										value={newVisitPeriod}
										onChange={(e) => setNewVisitPeriod(e.target.value)}
										placeholder="e.g., weekly, monthly"
									/>
									<IconButton
										onClick={() =>
											addArrayItem(
												"visitsPerPeriod",
												newVisitPeriod,
												setNewVisitPeriod,
											)
										}
									>
										<AddIcon />
									</IconButton>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.treatmentConfigOptions?.visitsPerPeriod?.map(
										(item, index) => (
											<Chip
												key={index}
												label={item}
												onDelete={() =>
													removeArrayItem("visitsPerPeriod", index)
												}
												deleteIcon={<DeleteIcon />}
											/>
										),
									)}
								</Box>
							</Grid>

							{/* Min Hours Between Visits */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Min Hours Between Visits
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										size="small"
										value={newMinHours}
										onChange={(e) => setNewMinHours(e.target.value)}
										placeholder="e.g., 24, 48"
									/>
									<IconButton
										onClick={() =>
											addArrayItem(
												"minHoursBetweenVisits",
												newMinHours,
												setNewMinHours,
											)
										}
									>
										<AddIcon />
									</IconButton>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.treatmentConfigOptions?.minHoursBetweenVisits?.map(
										(item, index) => (
											<Chip
												key={index}
												label={item}
												onDelete={() =>
													removeArrayItem("minHoursBetweenVisits", index)
												}
												deleteIcon={<DeleteIcon />}
											/>
										),
									)}
								</Box>
							</Grid>

							{/* Duration */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Duration
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										size="small"
										value={newDuration}
										onChange={(e) => setNewDuration(e.target.value)}
										placeholder="e.g., 30 days, 60 days"
									/>
									<IconButton
										onClick={() =>
											addArrayItem("duration", newDuration, setNewDuration)
										}
									>
										<AddIcon />
									</IconButton>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.treatmentConfigOptions?.duration?.map(
										(item, index) => (
											<Chip
												key={index}
												label={item}
												onDelete={() => removeArrayItem("duration", index)}
												deleteIcon={<DeleteIcon />}
											/>
										),
									)}
								</Box>
							</Grid>

							{/* Behavior */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Behavior (Events)
								</Typography>
								<BehaviorSelector
									label="Treatment Plan Behaviors"
									placeholder="Choose events that represent behaviors"
									helperText="Select from available events - each event represents a behavior type that will be available in treatment configurations"
									value={formData.treatmentConfigOptions?.behavior || []}
									onChange={(behaviorIds) => {
										if (formData.treatmentConfigOptions) {
											setFormData({
												...formData,
												treatmentConfigOptions: {
													...formData.treatmentConfigOptions,
													behavior: behaviorIds,
												},
											});
										}
									}}
									events={events}
									loading={eventsLoading}
								/>
							</Grid>

							{/* Custom Plan Allowed */}
							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.customPlanAllowed || false}
											onChange={(e) =>
												setFormData({
													...formData,
													customPlanAllowed: e.target.checked,
												})
											}
										/>
									}
									label="Allow Custom Plans"
									sx={{ mt: 1 }}
								/>
								<Typography
									variant="caption"
									display="block"
									color="text.secondary"
								>
									When enabled, users can create custom treatment configurations
									for this plan
								</Typography>
							</Grid>

							{/* SIMS-8910 (FR-1/FR-2): missed-entry generation.
							    Rendered inside the WEEKLY_CALENDAR branch, which is what keeps it
							    off Smart Program plans (AC3) — those render their own section and
							    never reach this block. */}
							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.disableMissedEntriesGeneration ?? false}
											onChange={(e) =>
												setFormData({
													...formData,
													disableMissedEntriesGeneration: e.target.checked,
												})
											}
											inputProps={{
												"aria-describedby":
													"disable-missed-entries-generation-help",
											}}
										/>
									}
									label="Disable Missed Entry Generation"
									sx={{ mt: 1 }}
								/>
								<Typography
									id="disable-missed-entries-generation-help"
									variant="caption"
									display="block"
									color="text.secondary"
								>
									When enabled, the nightly process stops creating missed
									entries for participants on this plan, including those already
									enrolled. Missed entries recorded before this was turned on
									stay in place and still require a result.
								</Typography>
							</Grid>

							{/* SIMS-7151/7159: plan-level segment editor (FR-3, FR-4, FR-6) */}
							<Grid item xs={12}>
								<SegmentEditor
									segments={formData.segments || []}
									onChange={(segments: TreatmentSegment[]) =>
										setFormData({ ...formData, segments })
									}
									rewardEngineOptions={rewardEngineOptions}
									behaviors={events || []}
									cmVisitScheduleOptions={
										formData.treatmentConfigOptions?.cmVisitScheduleOptions ||
										[]
									}
									templateDefaults={segmentTemplateDefaults}
								/>
							</Grid>
						</>
					)}

					{/* SIMS-9026 (FR-2, IR-4): the plan-level threshold propagation option.

					    Offered for EVERY program type, with no special case.

					    An earlier version disabled it on Smart Program plans (an AC5 this plan
					    proposed, never in the ticket, never confirmed by the PO). Dropped: it
					    guarded nothing. Propagation is only ever offered when a THRESHOLD FIELD
					    moves, and a Smart Program plan has none to move — no configuration
					    templates and no segments, verified across all 11 such plans on dev. So the
					    confirmation could never open there whatever this checkbox said, and the
					    disable bought a caption rather than a safeguard, at the price of a
					    programType branch to unwind once every enrollment carries a config
					    snapshot (PO, 2026-09-17). */}
					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Checkbox
									checked={
										formData.propagateThresholdToCurrentEnrollments || false
									}
									onChange={(e) =>
										setFormData({
											...formData,
											propagateThresholdToCurrentEnrollments: e.target.checked,
										})
									}
									inputProps={{
										"aria-label":
											"Apply disenrollment threshold changes to current enrollments",
									}}
								/>
							}
							label="Apply disenrollment threshold changes to current enrollments"
							sx={{ mt: 1 }}
						/>
						<Typography
							variant="caption"
							display="block"
							color="text.secondary"
						>
							When enabled, changing this plan&apos;s disenrollment threshold
							also applies to participants who are already enrolled. When
							disabled, a change affects future enrollments only.
						</Typography>

						{/* SIMS-9024 (SIMS-9037/9038) — the changes already applied to this plan.
						    Placed beside the option it describes: an administrator reading "what
						    does this do to current participants" is in the right frame to ask
						    "what has it done before".

						    ALWAYS rendered in edit mode, deliberately NOT conditional on a
						    propagation existing. PropagationBanner returns null when there is no
						    job (propagationStatus.ts), so hanging this off the banner would mean a
						    plan that has never had a change offers no way in — and SIMS-9037 AC5's
						    empty state would be unreachable. The empty state lives BEHIND the
						    button, not instead of it. */}
						{isEditMode && editingPlan && (
							<Button
								size="small"
								sx={{ mt: 1 }}
								data-testid="threshold-history-open"
								onClick={() => setHistoryOpen(true)}
							>
								Threshold change history
							</Button>
						)}
					</Grid>

					<Grid item xs={12}>
						{saveError && (
							<Alert
								severity="error"
								sx={{ mb: 2 }}
								data-testid="treatment-plan-save-error"
							>
								{saveError}
							</Alert>
						)}
						<Box sx={{ display: "flex", gap: 2 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmit}
								disabled={createMutation.isPending || updateMutation.isPending}
							>
								{createMutation.isPending || updateMutation.isPending
									? "Saving..."
									: isEditMode
										? "Update Treatment Plan"
										: "Create Treatment Plan"}
							</Button>
							<Button variant="outlined" onClick={onClose}>
								Cancel
							</Button>
						</Box>
					</Grid>
				</Grid>
			</CardContent>

			{/* SIMS-9031: the confirmation between the administrator and a change that can end
			    participants' enrollments (D2). Rendered here rather than inside the save handler so
			    cancelling leaves the plan edit saved but applies nothing to participants (AC6). */}
			{pendingPropagation && (
				<ThresholdPropagationModal
					open
					forecast={pendingPropagation.forecast}
					forecastDisenrolled={pendingPropagation.forecastDisenrolled}
					previousValue={pendingPropagation.previousValue}
					nextValue={pendingPropagation.nextValue}
					fieldLabel={pendingPropagation.fieldLabel}
					propagationInProgress={pendingPropagation.propagationInProgress}
					forecastChanged={pendingPropagation.forecastChanged}
					isSubmitting={propagationSubmitting}
					// SIMS-9030 AC3: including them changes the counts, and the acknowledged number
					// has to match what will happen — so the forecast is recomputed server-side
					// rather than adjusted in the page.
					onIncludeCustomisedChange={async (include) => {
						if (!editingPlan) {
							return;
						}
						try {
							const refreshed =
								await treatmentPlanService.previewThresholdPropagation(
									editingPlan._id,
									{
										templateName: pendingPropagation.templateName,
										templateId: pendingPropagation.templateId,
										field: pendingPropagation.field,
										segmentWeekRange: pendingPropagation.segmentWeekRange,
										nextThreshold: pendingPropagation.nextValue,
										templateThresholdBeforeSave:
											pendingPropagation.previousValue,
										includeCustomised: include,
									},
								);
							setPendingPropagation({
								...pendingPropagation,
								forecast: refreshed,
								forecastDisenrolled: refreshed.forecastDisenrolled ?? [],
								propagationInProgress: refreshed.propagationInProgress,
								// AC7 / D2: this recomputed the figure the administrator is being asked
								// to acknowledge, so the modal must say so and require it to be typed
								// again. Without this the count can move under a still-valid-looking
								// acknowledgment.
								forecastChanged:
									refreshed.forecastDisenrollments !==
									pendingPropagation.forecast.forecastDisenrollments,
							});
						} catch (err) {
							// eslint-disable-next-line no-console
							console.error("Could not refresh the propagation forecast:", err);
							// The forecast on screen no longer reflects the ticked option, so it must
							// not be confirmable: flag it as changed rather than leaving a stale figure
							// looking authoritative.
							setPendingPropagation({
								...pendingPropagation,
								forecastChanged: true,
							});
							alert(
								"Could not recalculate who this change affects. Close the confirmation and try again.",
							);
						}
					}}
					onCancel={() => {
						// AC6: nothing was persisted, so there is nothing to undo. The form stays
						// open and dirty, which is what lets the administrator untick propagation
						// and save the plan change on its own.
						setPendingPropagation(null);
					}}
					onConfirm={async (acknowledgedDisenrollments, includeCustomised) => {
						if (!editingPlan) {
							return;
						}
						// CR-1 / SIMS-9036 AC1: the change must be attributable to a real person.
						// Refuse rather than send a knowingly-invalid actor — the backend DTO rejects
						// an empty id with a 400, so `?? ""` only turned "this must not proceed" into
						// an unexplained failure the administrator could not act on.
						if (!userInfo?.id) {
							alert(
								"Your user account could not be identified, so this change has not been " +
									"applied. Sign in again and retry — a threshold change must be " +
									"recorded against the administrator who made it.",
							);
							return;
						}
						setPropagationSubmitting(true);
						try {
							// Option 1 ordering: the propagation goes FIRST, because it opens the Job
							// — so from this moment every outcome is recorded and, if anything fails,
							// visible on the status banner and retryable (SIMS-9033/9034).
							//
							// Doing it the other way round leaves a gap: if the plan saved and the
							// apply call never reached the server, the plan would hold a threshold
							// the participants do not, with no Job to explain or retry it. That is
							// precisely the drift AC6 exists to prevent.
							//
							// Safe in this order because apply() takes its values from the request
							// and derives the cohort from ENROLLMENT snapshots — it never reads the
							// plan document, so it does not care that the plan is not saved yet.
							await treatmentPlanService.applyThresholdPropagation(
								editingPlan._id,
								{
									templateName: pendingPropagation.templateName,
									templateId: pendingPropagation.templateId,
									field: pendingPropagation.field,
									segmentWeekRange: pendingPropagation.segmentWeekRange,
									nextThreshold: pendingPropagation.nextValue,
									templateThresholdBeforeSave: pendingPropagation.previousValue,
									acknowledgedDisenrollments,
									includeCustomised,
									// SIMS-9036 AC1 / CR-1: the real administrator, recorded on the
									// change record and on every participant's history entry.
									actorId: userInfo?.id ?? "",
									// FR-19: the option as THIS save sets it. AC6 writes the plan only
									// after this call returns, so the persisted flag is still false on
									// a first-time opt-in and the server would otherwise refuse the
									// change the administrator just acknowledged. Taken from planData,
									// which is exactly what gets persisted a moment later.
									propagateThresholdToCurrentEnrollments:
										pendingPropagation.planData
											.propagateThresholdToCurrentEnrollments,
								},
							);

							// AC6: the plan is written only now, on confirmation, and it is exactly
							// what the forecast was computed from.
							await updateMutation.mutateAsync({
								id: editingPlan._id,
								data: pendingPropagation.planData,
							});

							setPendingPropagation(null);
							setPropagationRefreshKey((key) => key + 1);
							onClose();
						} catch (err) {
							// AC7: the server recomputes the forecast before writing and refuses a
							// stale acknowledgment, returning the current figures. Show those and
							// ask again rather than silently applying something else.
							const refreshed = (
								err as
									| {
											forecast?: PropagationForecast & {
												forecastDisenrolled?: ForecastDisenrollment[];
											};
									  }
									| undefined
							)?.forecast;
							if (refreshed) {
								setPendingPropagation({
									...pendingPropagation,
									forecast: refreshed,
									// The list must move with the counts: showing the old participants
									// beside corrected numbers would be worse than showing none.
									forecastDisenrolled: refreshed.forecastDisenrolled ?? [],
									forecastChanged: true,
								});
							} else {
								// eslint-disable-next-line no-console
								console.error("Threshold propagation failed:", err);
							}
						} finally {
							setPropagationSubmitting(false);
						}
					}}
				/>
			)}
		</Card>
	);
};

export default TreatmentPlanForm;
